import React from 'react'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import { Id } from 'types/definitions'
import { UserDetailsViewModel } from 'user-management/views/UserDetails/UserDetailsViewModel'
import { formatToLongDateWithComma } from 'utils/date-util'
import { pluralize } from 'utils/pluralization-util'
import styles from './UserDetailsInformation.sass'
import Workspace from '../../../iam/models/Workspace'
import { formatUserDataType } from '../../formatters/user-data-type-formatter'

const { isSupported, copy } = require('clipbrd')

interface UserDetailsInformationProps {
  vm: UserDetailsViewModel
}

export default observer(({ vm }: UserDetailsInformationProps) => {
  const user = vm.user
  const provider = vm.provider
  const publicId = user.publicId ? Id.assert(user.publicId) : ''

  const copyTextToClipboard = (text: string) => {
    if (isSupported()) {
      copy(text)
    }
  }

  const createCopyMethod = (text: string) => () => copyTextToClipboard(text)

  return (
    <>
      <UserInfoCard
        title="User information"
        actions={
          vm.availableWorkspaces.length > 0 && (
            <DropdownItem
              onClick={() =>
                vm.editUserInformationVM.open(
                  vm.user,
                  vm.availableWorkspaces[0],
                )
              }
              dataTest="user-details-info-actions-edit-user-information"
            >
              Edit user information
            </DropdownItem>
          )
        }
      >
        <UserInfoLine title="Full name" value={user.displayName} />
        <UserInfoLine
          title="Email address"
          value={user.email}
          buttonText="Copy"
          buttonAction={() => copyTextToClipboard(user.email)}
        />
        <UserInfoLine
          title="Phone"
          value={user.phone}
          buttonText="Copy"
          buttonAction={() => copyTextToClipboard(user.phone)}
        />
        <UserInfoLine
          title="ID"
          value={publicId}
          buttonText="Copy"
          buttonAction={() => copyTextToClipboard(publicId)}
        />
        <UserInfoLine title="Active jobs" value={vm.activeJobs} />
        <UserInfoLine title="Total jobs" value={vm.totalJobs} />
        <UserInfoLine
          title="Last login"
          value={formatToLongDateWithComma(user.lastSeen)}
        />
        <UserInfoLine
          title="Last user update"
          value={formatToLongDateWithComma(user.dateModified)}
        />
        <UserInfoLine
          title="Date joined"
          value={formatToLongDateWithComma(user.dateCreated)}
        />
        <UserInfoLine title="Legacy ID" value={user.userId} />
        <UserInfoLine
          title={`Payment ${pluralize('method', vm.paymentMethods.length)}`}
          value={vm.paymentMethods.map((pm) => pm.last4).join(', ') || 'None'}
        />
        {vm.user.userDataType === 'PRO' &&
          (vm.payoutMethodInfo.status === 'Connected' ? (
            <UserInfoLine
              title={
                vm.payoutMethodInfo.stripeAccountId
                  ? 'Payout method'
                  : 'Payout method status'
              }
              value={vm.payoutMethodInfo.stripeAccountId || 'Connected'}
              {...(vm.payoutMethodInfo.stripeAccountId && {
                buttonText: 'Copy',
                buttonAction: createCopyMethod(
                  vm.payoutMethodInfo.stripeAccountId,
                ),
              })}
            />
          ) : vm.payoutMethodInfo.status === 'NotConnected' ? (
            <UserInfoLine title="Payout method" value="None" />
          ) : (
            <UserInfoLine title="Payout method" value="Unknown" />
          ))}
        <UserInfoLine
          title="Classification"
          value={formatUserDataType(user.userDataType)}
        />
      </UserInfoCard>

      {vm.isUserDataTypePro && (
        <UserInfoCard
          title="Pro information"
          actions={
            vm.isPlatformAdmin && (
              <DropdownItem
                onClick={() =>
                  vm.editProDetailsVM.open(Id.assert(user.publicId))
                }
                dataTest="user-details-pro-information-actions-edit"
              >
                Edit Pro details
              </DropdownItem>
            )
          }
        >
          <UserInfoLine
            title="Global capacity"
            value={`${vm.activeJobs} / ${parseCapacity(provider.globalJobCapacity)}`}
            buttonText={vm.isPlatformAdmin ? 'Edit' : ''}
            buttonAction={() =>
              vm.manageUserCapacityVM.showWithUser(user, Workspace.none)
            }
            data-test="user-details-pro-information-manage-global-capacity"
          />
          <UserInfoLine title="Location" value={provider.stateRegion} />
          <UserInfoLine title="PTIN" value={provider.proPtinStatus} />
          <UserInfoLine
            title="Years of experience"
            value={provider.yearsExperience}
          />
          <UserInfoLine
            title="Software owned"
            value={provider.softwareOwned.length}
            buttonText="View"
            buttonAction={() =>
              vm.userInfoItemListVM.showWith({
                title: 'Software owned',
                userInfoItems: provider.softwareOwned,
              })
            }
            data-test="user-details-pro-information-software-owned-view-button"
          />
          <UserInfoLine
            title="Software experience"
            value={provider.softwareExperience.length}
            buttonText="View"
            buttonAction={() =>
              vm.userInfoItemListVM.showWith({
                title: 'Software experience',
                userInfoItems: provider.softwareExperience,
              })
            }
            data-test="user-details-pro-information-software-experience-view-button"
          />
          <UserInfoLine
            title="Specialties"
            value={provider.specialties.length}
            buttonText="View"
            buttonAction={() =>
              vm.userInfoItemListVM.showWith({
                title: 'Specialties',
                userInfoItems: provider.specialties,
              })
            }
            data-test="user-details-pro-information-specialties-view-button"
          />
        </UserInfoCard>
      )}
    </>
  )
})

interface UserDetailsCardProps {
  title: string
  actions?: React.ReactNode
  children: React.ReactNode
}

const UserInfoCard = observer(
  ({ title, actions, children }: UserDetailsCardProps) => {
    return (
      <div className={styles.userInfoCard}>
        <div className={styles.userInfoTitle}>
          <span>{title}</span>
          {actions && (
            <Dropdown
              autoDismiss
              position="bottom-end"
              button={
                <IconButton dataTest={'user-details-info-actions'}>
                  <Icon name="more" color="var(--grey600)" />
                </IconButton>
              }
            >
              <DropdownTitle>
                <span>Actions</span>
              </DropdownTitle>
              {actions}
            </Dropdown>
          )}
        </div>
        {children}
      </div>
    )
  },
)

interface UserInfoLineProps {
  title: string
  value: any
  buttonText?: string
  buttonAction?: () => void
  'data-test'?: string
}

const UserInfoLine = observer(
  ({
    title,
    value,
    buttonText,
    buttonAction,
    'data-test': dataTest,
  }: UserInfoLineProps) => {
    const button = buttonText && buttonAction
    const normalizeTitle = title.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className={styles.userInfoLine}>
        <span className={styles.infoLineTitle}>{title}</span>
        <div className={styles.infoLineSubtitle}>
          <span
            className={styles.infoLineValue}
            data-test={`user-info-line-${normalizeTitle}-value`}
          >
            {value}
          </span>
          {button && (
            <Button
              className={styles.infoLineButton}
              type="text"
              onClick={buttonAction}
              data-test={dataTest}
            >
              {buttonText}
            </Button>
          )}
        </div>
      </div>
    )
  },
)

export function parseCapacity(capacity?: number) {
  return capacity === 0 ? '∞' : capacity
}
